// Pairing codes for device sync: the few parts of a WebRTC session description that differ per
// connection (ICE username and password, the DTLS certificate fingerprint, and the host
// candidates), packed small enough for a QR code. The full SDP is rebuilt on the other side.
// The fingerprint in the code authenticates the encrypted channel, so nobody else on the
// network can join it. Ported from HRT Diary, whose NOTES explain the browser quirks.

const FORMAT = 1;
const CODE_PREFIX = 'rel1-';

const enc = new TextEncoder();
const dec = new TextDecoder();

export interface Candidate {
  addr: string;
  port: number;
}

export interface SdpParts {
  ufrag: string;
  pwd: string;
  fingerprint: string;
  candidates: Candidate[];
  mid: string;
}

/**
 * offer / answer: two-code pairing. reconnect: an offer to one remembered device, naming this
 * device (from) and that one (to), which connects without sending a reply. addresses: the
 * fallback reply to a reconnect when the scanning device can't reach the other one.
 */
export type Signal =
  | ({ type: 'offer' | 'answer' } & SdpParts)
  | ({ type: 'reconnect'; from: string; to: string } & SdpParts)
  | { type: 'addresses'; from: string; to: string; candidates: Candidate[] };

export type SignalType = Signal['type'];

/** Pull what the other device needs out of a local SDP. */
export function parseSdp(sdp: string): SdpParts {
  const get = (re: RegExp) => (sdp.match(re) || [])[1];
  const ufrag = get(/^a=ice-ufrag:(\S+)/m);
  const pwd = get(/^a=ice-pwd:(\S+)/m);
  const fp = get(/^a=fingerprint:sha-256 (\S+)/im);
  const mid = get(/^a=mid:(\S+)/m) || '0';
  if (!ufrag || !pwd || !fp) throw new Error('This browser’s connection details are in an unexpected format.');
  const candidates: Candidate[] = [];
  for (const m of sdp.matchAll(/^a=candidate:\S+ 1 udp \d+ (\S+) (\d+) typ host/gim)) {
    if (!candidates.some((c) => c.addr === m[1] && c.port === Number(m[2]))) candidates.push({ addr: m[1], port: Number(m[2]) });
  }
  return { ufrag, pwd, fingerprint: fp.toUpperCase(), candidates, mid };
}

/**
 * Rebuild a data-channel-only SDP from parseSdp() output. mid must match the offer's: Chrome and
 * Safari use "0", but Firefox has used names like "sdparta_0".
 */
export function buildSdp({ ufrag, pwd, fingerprint, candidates, mid = '0' }: SdpParts, type: SignalType, { moreCandidates = false } = {}): string {
  return [
    'v=0',
    `o=- ${Math.floor(Math.random() * 1e15)} 2 IN IP4 127.0.0.1`,
    's=-',
    't=0 0',
    `a=group:BUNDLE ${mid}`,
    'a=msid-semantic: WMS',
    'm=application 9 UDP/DTLS/SCTP webrtc-datachannel',
    'c=IN IP4 0.0.0.0',
    ...candidates.map((c, i) => `a=candidate:${i + 1} 1 udp ${2122260223 - i} ${c.addr} ${c.port} typ host generation 0`),
    ...(moreCandidates ? ['a=ice-options:trickle'] : ['a=end-of-candidates']),
    `a=ice-ufrag:${ufrag}`,
    `a=ice-pwd:${pwd}`,
    `a=fingerprint:sha-256 ${fingerprint}`,
    `a=setup:${type === 'answer' ? 'active' : 'actpass'}`,
    `a=mid:${mid}`,
    'a=sctp-port:5000',
    'a=max-message-size:262144',
    '',
  ].join('\r\n');
}

export const MDNS = /^([0-9a-f]{8})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{12})\.local$/i;
const IPV4 = /^\d+\.\d+\.\d+\.\d+$/;

const KINDS: SignalType[] = ['offer', 'answer', 'reconnect', 'addresses'];
const HAS_MID = 0x80; // set on the kind byte when the offer's mid isn't "0"; the mid follows pwd
const NOT_A_CODE = 'That isn’t a Relationships sync code.';

const uuidHex = (id: string) => {
  const hx = String(id || '').replace(/-/g, '');
  if (!/^[0-9a-f]{32}$/i.test(hx)) throw new Error('Unexpected device id.');
  return hx;
};
const hexUuid = (hx: string) => `${hx.slice(0, 8)}-${hx.slice(8, 12)}-${hx.slice(12, 16)}-${hx.slice(16, 20)}-${hx.slice(20)}`;

export function hexBytes(hex: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < hex.length; i += 2) out.push(parseInt(hex.slice(i, i + 2), 16));
  return out;
}

export const toHex = (buf: ArrayBuffer | Uint8Array) => [...new Uint8Array(buf)].map((x) => x.toString(16).padStart(2, '0')).join('');

/** A signal → bytes, for the QR code. */
export function encodeSignal(sig: Signal): Uint8Array {
  const kind = KINDS.indexOf(sig.type);
  if (kind < 0) throw new Error('Unknown code type.');
  const mid = sig.type !== 'answer' && sig.type !== 'addresses' && sig.mid && sig.mid !== '0' ? sig.mid : null;
  const out: number[] = [FORMAT, kind | (mid ? HAS_MID : 0)];
  const str = (s: string) => {
    const b = enc.encode(s);
    if (b.length > 255) throw new Error('Connection detail too long.');
    out.push(b.length, ...b);
  };
  const ids = (s: { from: string; to: string }) => out.push(...hexBytes(uuidHex(s.from)), ...hexBytes(uuidHex(s.to)));
  if (sig.type === 'addresses') {
    ids(sig);
    pushCandidates(out, sig.candidates, str);
    return Uint8Array.from(out);
  }
  str(sig.ufrag);
  str(sig.pwd);
  if (mid) str(mid);
  const fp = sig.fingerprint.split(':').map((x) => parseInt(x, 16));
  if (fp.length !== 32) throw new Error('Unexpected certificate fingerprint.');
  out.push(...fp);
  if (sig.type === 'reconnect') ids(sig);
  pushCandidates(out, sig.candidates, str);
  return Uint8Array.from(out);
}

function pushCandidates(out: number[], candidates: Candidate[], str: (s: string) => void) {
  // Real IPv4 addresses first, then private .local names (which the other device has to look
  // up on the network, and often can't), then anything else (IPv6).
  const rank = (a: string) => (IPV4.test(a) ? 0 : MDNS.test(a) ? 1 : 2);
  const cands = [...candidates].sort((a, b) => rank(a.addr) - rank(b.addr)).slice(0, 6);
  out.push(cands.length);
  for (const c of cands) {
    const m = c.addr.match(MDNS);
    if (m) out.push(1, ...hexBytes(m.slice(1).join('')));
    else if (IPV4.test(c.addr)) out.push(2, ...c.addr.split('.').map(Number));
    else {
      out.push(0);
      str(c.addr);
    }
    out.push(c.port >> 8, c.port & 255);
  }
}

export function decodeSignal(bytes: Uint8Array | number[]): Signal {
  const b = bytes instanceof Uint8Array ? bytes : Uint8Array.from(bytes);
  let i = 0;
  const need = (n: number) => {
    if (i + n > b.length) throw new Error(NOT_A_CODE);
  };
  const byte = () => (need(1), b[i++]);
  const take = (n: number) => (need(n), b.slice(i, (i += n)));
  const str = () => dec.decode(take(byte()));
  if (byte() !== FORMAT) throw new Error('That isn’t a Relationships sync code, or it’s from a different version of the app.');
  const head = byte();
  const kind = head & ~HAS_MID;
  if (kind >= KINDS.length) throw new Error(NOT_A_CODE);
  const hex = (n: number) => [...take(n)].map((x) => x.toString(16).padStart(2, '0')).join('');
  const type = KINDS[kind];
  let parts: Omit<SdpParts, 'candidates'> | null = null;
  let ids: { from: string; to: string } | null = null;
  if (type === 'addresses') {
    ids = { from: hexUuid(hex(16)), to: hexUuid(hex(16)) };
  } else {
    const ufrag = str();
    const pwd = str();
    const mid = head & HAS_MID ? str() : '0';
    const fingerprint = [...take(32)].map((x) => x.toString(16).padStart(2, '0').toUpperCase()).join(':');
    parts = { ufrag, pwd, mid, fingerprint };
    if (type === 'reconnect') ids = { from: hexUuid(hex(16)), to: hexUuid(hex(16)) };
  }
  const candidates: Candidate[] = [];
  for (let n = byte(); n > 0; n--) {
    const t = byte();
    let addr: string;
    if (t === 1) addr = `${hexUuid(hex(16))}.local`;
    else if (t === 2) addr = [...take(4)].join('.');
    else if (t === 0) addr = str();
    else throw new Error(NOT_A_CODE);
    const port = (byte() << 8) | byte();
    candidates.push({ addr, port });
  }
  if (i !== b.length) throw new Error(NOT_A_CODE);
  if (type === 'addresses') return { type, candidates, ...ids! };
  if (type === 'reconnect') return { type, candidates, ...parts!, ...ids! };
  return { type, candidates, ...parts! };
}

/** Bytes → text code for copy and paste. */
export function signalToText(bytes: Uint8Array): string {
  let s = '';
  for (const x of bytes) s += String.fromCharCode(x);
  return CODE_PREFIX + btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function textToSignal(text: string): Uint8Array {
  const t = String(text || '').replace(/\s+/g, '');
  if (!t.toLowerCase().startsWith(CODE_PREFIX)) throw new Error(`${NOT_A_CODE} It should start with “${CODE_PREFIX}”.`);
  let bin: string;
  try {
    bin = atob(t.slice(CODE_PREFIX.length).replace(/-/g, '+').replace(/_/g, '/'));
  } catch {
    throw new Error('That sync code is incomplete. Copy it again.');
  }
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export const CODE_PLACEHOLDER = `${CODE_PREFIX}…`;

/** ICE username and password for the reply to a reconnect offer, from the shared secret. */
export async function reconnectCredentials(secret: string, offerUfrag: string, offerPwd: string): Promise<{ ufrag: string; pwd: string }> {
  const key = await crypto.subtle.importKey('raw', Uint8Array.from(hexBytes(secret)), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const mac = toHex(await crypto.subtle.sign('HMAC', key, enc.encode(`${offerUfrag}\n${offerPwd}`)));
  return { ufrag: mac.slice(0, 8), pwd: mac.slice(8, 40) };
}

/** Both devices derive the same secret from the two random nonces in their hellos. */
export async function pairSecret(nonceA: string, nonceB: string): Promise<string> {
  return toHex(await crypto.subtle.digest('SHA-256', enc.encode([nonceA, nonceB].sort().join(':'))));
}

/** "iPhone · Safari", "Mac · Chrome", … from the user agent; only shown to the person. */
export function deviceName(ua = globalThis.navigator ? navigator.userAgent : '', touch = globalThis.navigator ? navigator.maxTouchPoints : 0): string {
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua) || (/Macintosh/.test(ua) && touch > 1)
      ? 'iPad'
      : /Macintosh/.test(ua)
        ? 'Mac'
        : /Android/.test(ua)
          ? 'Android'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'Device';
  const br = /Edg\//.test(ua) ? 'Edge' : /CriOS|Chrome\//.test(ua) ? 'Chrome' : /FxiOS|Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : '';
  return br ? `${os} · ${br}` : os;
}
